/* ═══════════════════════════════════════════════════════════════════════════
   Secousse d'écran — modèle trauma².

   Trois décisions, chacune contre une erreur classique :

   1. On accumule du TRAUMA, jamais une amplitude. L'amplitude affichée vaut
      trauma², ce qui rend les petits impacts discrets et les gros violents avec
      une seule grandeur. Une amplitude linéaire donne soit du bruit permanent
      sur le popcorn, soit des explosions qui ne se sentent pas.

   2. Le trauma décroît LINÉAIREMENT (1,8 par seconde) et non exponentiellement.
      Une décroissance exponentielle ne s'annule jamais tout à fait : l'écran
      tremblote en permanence à un niveau imperceptible mais fatigant, et le
      joueur ne sait pas dire pourquoi il est mal à l'aise.

   3. Le déplacement suit un BRUIT LISSÉ, pas Math.random(). Le bruit blanc
      grésille — chaque frame saute n'importe où, ce qui se lit comme un défaut
      d'affichage. Un bruit interpolé secoue : les positions successives sont
      corrélées, donc le mouvement a une direction.

   L'amplitude est plafonnée à 1 % de la hauteur du terrain (PLAN.md §3.6). Une
   secousse plus forte masque les projectiles, et dans un jeu où l'on esquive au
   pixel, un effet qui cache le danger est un bug de conception.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Bruit de valeur lissé, déterministe pour une graine donnée.
 *  On reste hors de src/sim/, donc Math.sin est autorisé ici — la secousse
 *  n'a aucun effet sur l'issue d'une partie. */
function hash1(n: number, seed: number): number {
  const s = Math.sin(n * 127.1 + seed) * 43758.5453;
  return s - Math.floor(s);
}

function valueNoise(x: number, seed: number): number {
  const i = Math.floor(x);
  const f = x - i;
  // Interpolation en smoothstep : dérivée nulle aux extrémités, donc aucun
  // angle visible au passage d'un échantillon au suivant.
  const u = f * f * (3 - 2 * f);
  return (hash1(i, seed) * (1 - u) + hash1(i + 1, seed) * u) * 2 - 1;
}

/** Deux octaves : une porteuse à 22 Hz pour le corps du mouvement, une
 *  harmonique à 47 Hz pour le grain. Une seule octave donne une oscillation
 *  trop régulière, qui se lit comme une animation plutôt que comme un choc. */
export function shakeNoise(t: number, seed: number): number {
  return valueNoise(t * 22, seed) * 0.65 + valueNoise(t * 47, seed + 91) * 0.35;
}

export class Shake {
  /** Trauma courant dans [0,1]. */
  private t = 0;

  get trauma(): number {
    return this.t;
  }
  /** Amplitude normalisée effectivement appliquée — c'est le carré du trauma. */
  get magnitude(): number {
    return this.t * this.t;
  }

  add(amount: number): void {
    this.t += amount;
    if (this.t > 1) this.t = 1;
    else if (this.t < 0) this.t = 0;
  }

  /** `dt` en secondes. Décroissance linéaire : le trauma atteint réellement
   *  zéro, et l'écran redevient parfaitement stable. */
  advance(dt: number, decayPerSec: number): void {
    this.t -= decayPerSec * dt;
    if (this.t < 0) this.t = 0;
  }

  /** Déplacement à appliquer à la caméra, en unités monde.
   *  `time` est un temps de présentation en secondes. */
  offsetX(time: number, maxAmplitude: number): number {
    return shakeNoise(time, 0) * this.magnitude * maxAmplitude;
  }
  offsetY(time: number, maxAmplitude: number): number {
    return shakeNoise(time, 53) * this.magnitude * maxAmplitude;
  }
  /** Roulis, en radians. Faible par construction : au-delà de ~0,5°, l'horizon
   *  bascule et la lecture des trajectoires en souffre. */
  roll(time: number, maxRadians: number): number {
    return shakeNoise(time, 117) * this.magnitude * maxRadians;
  }

  reset(): void {
    this.t = 0;
  }
}
